<template>
    <div class="d-flex gap20">

      <div style="flex: 2;">
        <div class="step-title-label">
          Connect your OpenAI API key
        </div>
        <p class="mt-3">When you connect your OpenAI API key, POP AI Writer reports will only cost <span class="font-weight-bold">{{popCreditCharges.aiWriterCreditsFlatRateOwnApiKey}} POP Credits</span> flat rate.</p>
      </div>

      <div style="flex: 5;">
        <div>
          <div class="flex-grow-1 flex flex-column">

            <!-- Manage key globally -->
            <!-- ------------------- -->

            <div class="global-openai-key-box">
              <template v-if="selectedSubAccountRelation">
                <div v-if="isUserOpenAIKeyEnabledGlobally" class="d-flex align-center gap10">
                  <i class="material-icons-outlined" style="color: #00AA64; font-size: 1.1rem;">check</i>
                  <span>Account owner has connected an OpenAI key.</span>
                </div>
                <div v-else class="d-flex align-center gap10">
                  <i class="material-icons-outlined" style="color: #FFBD60; font-size: 1.1rem;">warning</i>
                  <span>Account owner did not set an active OpenAI key.</span>
                </div>
              </template>

              <template v-else>
                <div class="d-flex align-center gap10 justify-space-between">
                  <div v-if="ownerUser.openAIKey">
                    <div v-if="ownerUser.useMyOwnApiKey" class="d-flex align-center gap10">
                      <i class="material-icons-outlined" style="color: #00AA64; font-size: 1.1rem;">check</i>
                      <span>You have an active OpenAI key on your profile.</span>
                    </div>
                    <div v-else class="d-flex align-center gap10">
                      <i class="material-icons-outlined" style="color: #FFBD60; font-size: 1.1rem;">warning</i>
                      <span>You have an OpenAI key on your profile, but it is disabled globally.</span>
                    </div>
                  </div>
                  <div v-else>
                    You do not have a connected OpenAI key.
                  </div>

                  <v-btn
                    small
                    class="button-with-icon text-none"
                    @click="showManageApiKeyModal = true"
                    :disabled="$store.getters.isDemoUser"
                  >
                    Manage key
                  </v-btn>
                </div>

                <div v-if="showManageApiKeyModal" class="modal" :class="{'is-active': true}">
                  <div class="modal-background" @click="showManageApiKeyModal = false"></div>
                  <div class="modal-content">
                    <div class="box modal-container">
                      <div class="modal-content-root" style="margin-top: 0;">
                        <div class="d-flex justify-space-between mb-6">
                          <div class="card-small-title" style="font-size: 1.2rem;">
                            Manage OpenAI key
                          </div>
                          <button aria-label="close" @click="showManageApiKeyModal = false">
                              <i class="material-icons">close</i>
                          </button>
                        </div>

                        <div class="d-flex align-center">
                          <v-text-field
                            class="gpt-key-text-field"
                            dense hide-details
                            placeholder="Input your GPT key"
                            color="greenColor"
                            v-model="globalOpenAIKeyFieldValue"
                            outlined
                          ></v-text-field>
                          <v-btn
                            color="profileAnchorColor"
                            class="gpt-key-save-btn text-none whiteColor--text"
                            :disabled="!isSomethingChanged"
                            @click="saveOpenAiKey">
                            <span>{{ ownerUser.openAIKey ? 'Update' : 'Connect' }}</span>
                          </v-btn>
                        </div>

                        <div class="mt-5 d-flex align-center" v-if="ownerUser.openAIKey">
                          <vue-toggles
                            :value="globalUseMyOwnApiKeyFieldValue"
                            width="40" height="20"
                            :checkedBg="$vuetify.theme.currentTheme['green7Color']"
                            :uncheckedBg="$vuetify.theme.currentTheme['gray35Color']"
                            @click="globalUseMyOwnApiKeyFieldValue = !globalUseMyOwnApiKeyFieldValue"
                            :disabled="$store.getters.isDemoUser"
                            >
                          </vue-toggles>
                          <div class="ml-2 mainColor--text">
                            {{ globalUseMyOwnApiKeyFieldValue ? 'Enable key globally': 'Disabled globally' }}
                          </div>
                        </div>

                        <div class="mt-5">
                          <v-btn
                            v-if="ownerUser.openAIKey"
                            color="redColor"
                            class="text-none whiteColor--text"
                            :disabled="$store.getters.isDemoUser"
                            @click="deleteOpenAiKey">
                            <span>Delete key</span>
                          </v-btn>
                        </div>
            
                        <div class="mt-5">
                          Need help? Follow our&nbsp;
                          <a href="https://help.pageoptimizer.pro/article/how-to-use-gpt-4-api-in-pop" target="_blank" style="text-decoration: underline; color: var(--v-mainColor3-base);">
                            How to use OpenAI API in POP Guide.
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </template>
            </div>

            <!-- Manage key usage for the current AI Run -->
            <!-- ------------------------------- -->
            <div class="mt-5 d-flex align-center">
              <vue-toggles
                :value="useMyOwnApiKey"
                width="40" height="20"
                :checkedBg="$vuetify.theme.currentTheme['green7Color']"
                :uncheckedBg="$vuetify.theme.currentTheme['gray35Color']"
                @click="$emit('update:useMyOwnApiKey', !useMyOwnApiKey)"
                :disabled="$store.getters.isDemoUser || !!aiRun"
                >
              </vue-toggles>
              <div class="ml-2">
                Use OpenAI key for the current AI run.
              </div>
            </div>
          </div>


          <div class="modal" :class="{'is-active': showApiKeyErrorModal}">
            <div class="modal-background" @click="showApiKeyErrorModal = false"></div>
            <div class="modal-content">
              <div class="box modal-container">
                <div class="modal-content-root">
                  <div class="subtitle-font mainColor--text text-center">
                    Sorry, it looks like the API key you are trying to add doesn't have GPT 4 access yet.  This is a common issue.  You'll need to go back into your OpenAI and select GPT 4 specifically.
                    <div class="mt-5 subtitle mainColor--text font-weight-bold text-center">{{ errorMsg }}</div>
                  </div>
                  <div class="d-flex justify-end">
                    <v-btn rounded
                      color="profileAnchorColor"
                      class="button-with-icon whiteColor--text text-none"
                      @click="showApiKeyErrorModal = false">
                      Ok
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
</template>

<script>
export default {
  name: 'ConfigApiKey',
  props: [
      'isDemo',
      'user',
      'ownerUser',
      'isMissionControlSession',
      'selectedSubAccountRelation',
      'useMyOwnApiKey',
      'aiRun',

      'popCreditCharges',
      'isUserOpenAIKeyEnabledGlobally',
  ],
  data () {
    return {
      globalOpenAIKeyFieldValue: '',
      globalUseMyOwnApiKeyFieldValue: false,

      showManageApiKeyModal: false,

      showApiKeyErrorModal: false,
      errorMsg: '',
    }
  },
  computed:  {
    isExistingApiKeyChanged () {
      if (!this.ownerUser || !this.ownerUser.openAIKey) {
        return false;
      }
      if (this.ownerUser.openAIKey != this.globalOpenAIKeyFieldValue) {
        return true;
      }
      return false;
    },
    isSomethingChanged () {
      if (!this.ownerUser.openAIKey && this.globalOpenAIKeyFieldValue) {
        return true;
      }
      return this.isExistingApiKeyChanged || this.globalUseMyOwnApiKeyFieldValue != this.ownerUser.useMyOwnApiKey
    }
  },
  methods: {
    updateDefaultValues() {
      this.globalOpenAIKeyFieldValue = this.ownerUser.openAIKey
      this.globalUseMyOwnApiKeyFieldValue = this.ownerUser.useMyOwnApiKey
    },
    loadUser () {
      return this.$store.dispatch('loadUser')
      .then((response) => {
        if (response.data.user) {
          this.updateDefaultValues()
          this.$store.commit('setUser', response.data.user)
        }
      })
    },
    saveOpenAiKey () {
      this.$store.commit('showLoading')
      this.$store.dispatch('addOpenAiKey', {
        apiKey: this.globalOpenAIKeyFieldValue.trim(),
        useMyOwnAPI: this.globalUseMyOwnApiKeyFieldValue,
      })
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.$notify({
            group: "info",
            type: "success",
            text: response.data.msg,
          });
          this.loadUser();
        } else if (response.data.status == 'FAILURE') {
          this.errorMsg = response.data.error
          this.showApiKeyErrorModal = true
          this.loadUser();
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading')
      })
    },
    deleteOpenAiKey () {
      this.$store.commit('showLoading')
      this.$store.dispatch('addOpenAiKey', {
        deleteKey: true,
      })
      .then(response => {
        if (response.data && response.data.status == 'SUCCESS') {
          this.$notify({
            group: "info",
            type: "success",
            text: response.data.msg,
          });
          this.loadUser();
        }
      })
      .finally(() => {
        this.$store.commit('hideLoading')
      })
    },
  },
  mounted () {
    if (this.ownerUser) {
      this.updateDefaultValues();
    }
  }
}
</script>

<style lang="scss" scoped>
.gpt-key-text-field {
  margin-top: -4px !important;

  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  ::v-deep {
    fieldset {
      border-width: 1px !important;
      border-right: none;
      border-color: var(--v-gray12Color-base);
    }
    input {
      color: var(--v-mainColor3-base);
    }
  }
}

.gpt-key-save-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.global-openai-key-box {
  padding:10px;
  border: 1px solid var(--v-grayColor-base);
  border-radius: 3px;
}
</style>